import type { Auth } from '@ixigo-packages/login/core';
import { createAuth } from '@ixigo-packages/login/core';
import { createIsomorphicFn } from '@tanstack/react-start';
import { getServerAuth } from './server-auth';

/**
 * The browser auth singleton (one per tab). SSR-safe: `createAuth` reads cookies
 * defensively, so on the server it resolves to `anonymous`. Passed to
 * `<AuthProvider>`; `browserAuth.interceptor` is wired into the shared
 * `@ixigo/shell/fetch` transport in `client.tsx`.
 *
 * Config-free by design: storage, refresh, and the transport are SDK-owned. The
 * surface-specific `clientId`/`apiKey` ride the host's `getCommonHeaders` on the
 * shared transport (resolved from the `clientId` cookie / UA per request); the
 * SDK core only owns the Authorization header (via `interceptor`) and refresh.
 */
let browserAuth: Auth | undefined;

/** Native launch cookies must be synchronized before the first SDK snapshot. */
export function getBrowserAuth(): Auth {
	browserAuth ??= createAuth();
	return browserAuth;
}

/**
 * Isomorphic auth accessor for SSR loaders / `beforeLoad` guards: a
 * request-scoped server auth on the server, the browser singleton on the client.
 * Always `await` it (the server branch is async).
 */
export const getAuth = createIsomorphicFn()
	.server(() => getServerAuth())
	.client(getBrowserAuth);
